import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import CountUp from "react-countup";

const stats = [
    { n: 10, suffix: "L+", label: "Patients Served", note: "Across pharmacy, clinic & EMR touchpoints" },
    { n: 10, suffix: "Cr+", prefix: "₹", label: "Patient Savings", note: "Through subsidised diagnostics & medicines" },
    { n: 33, suffix: "", label: "Railway EMRs", note: "24/7 emergency medical rooms" },
    { n: 100, suffix: "+", label: "Cardiac Lives Saved", note: "From the first response onwards" },
    { n: 3000, suffix: "+", label: "Railway Trauma Victims", note: "Treated free of cost during the Golden Hour" },
    { n: 2800, suffix: "+", label: "Hospital Beds Managed", note: "Across 13 partner & owned facilities" },
    { n: 50, suffix: "%", label: "Cost Reduction", note: "Average on medicines and diagnostics" },
    { n: 10, suffix: "", label: "Emergency Deliveries", note: "Conducted successfully at railway stations" },
];

const StatCard = ({ s, idx, start }) => {
    const isGreen = idx % 3 === 1;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="group p-8 md:p-10 border border-ink-900/10 bg-paper-50 hover:border-bisleri/40 hover:shadow-xl hover:shadow-bisleri/5 transition-all duration-500"
            data-testid={`impact-stat-${idx}`}
        >
            <div className="eyebrow text-ink-500 group-hover:text-bisleri transition-colors">
                {String(idx + 1).padStart(2, "0")} /
            </div>
            <div className="mt-4 font-display text-5xl md:text-6xl lg:text-7xl text-ink-900 tracking-tight leading-none">
                {s.prefix || ""}
                {start ? <CountUp end={s.n} duration={2.4} separator="," /> : 0}
                <span className={isGreen ? "text-clinicgreen-deep" : "text-bisleri"}>{s.suffix}</span>
            </div>
            <div className="mt-5 font-display italic text-lg md:text-xl text-ink-900">
                {s.label}
            </div>
            <div className="mt-2 font-body text-xs md:text-sm text-ink-600 leading-relaxed">
                {s.note}
            </div>
        </motion.div>
    );
};

const Impact = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-150px" });
    const [start, setStart] = useState(false);
    useEffect(() => {
        if (inView) setStart(true);
    }, [inView]);

    return (
        <section
            id="impact"
            ref={ref}
            data-testid="impact-section"
            className="relative py-24 md:py-32 lg:py-40 bg-sage"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="section-number">03 / Impact</div>
                        <div className="mt-6 divider" />
                        <div className="mt-4 eyebrow">By the Numbers</div>
                    </div>

                    <div className="col-span-12 lg:col-span-8">
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink-900 max-w-3xl">
                            Numbers that <em className="text-bisleri">refuse to be forgotten</em> —
                            because behind each is a life.
                        </h2>
                    </div>
                </div>

                <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {stats.map((s, i) => (
                        <StatCard s={s} idx={i} key={s.label} start={start} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Impact;
